import { useMemo } from "react";
import { useMutation, useQuery } from "react-query";

import type { CustomAttributeDefinition } from "@jti/fe-common";
import type { AdditionalFieldResponseType, CustomAttributeWriteResponse } from "@jti/rest-api-types";

import { MUTATION_KEYS, QUERY_KEYS } from "../../../constants/queryKeys";
import { CasCustomAttrsApi, sortCustomAttributes } from "../../../services";

type CustomAttrs = {
  isLoadingCustomAttrs: boolean;
  additionalFields?: AdditionalFieldResponseType;
  orderedCustomAttributes?: CustomAttributeDefinition[];
};

export const useCustomAttributesQuery = () => {
  const casCustomAttrsApi = CasCustomAttrsApi.init();
  return useQuery<AdditionalFieldResponseType, Error>(
    QUERY_KEYS.customAttributes,
    async (): Promise<AdditionalFieldResponseType> => {
      return await casCustomAttrsApi.getCustomAttributes();
    },
    {
      staleTime: Infinity,
      cacheTime: Infinity,
      retry: false,
    }
  );
};

export const useUpdateCustomAttribute = () => {
  const casCustomAttrsApi = CasCustomAttrsApi.init();
  return useMutation<CustomAttributeWriteResponse, Error, Record<string, string | undefined>>(
    MUTATION_KEYS.updateCustomAttributes,
    (data: Record<string, string | undefined>) => casCustomAttrsApi.write(data)
  );
};

export const useCustomAttributes = (): CustomAttrs => {
  const { isLoading: isLoadingCustomAttrs, data: additionalFields } = useCustomAttributesQuery();

  const orderedCustomAttributes = useMemo(() => {
    return sortCustomAttributes(additionalFields?.data || []);
  }, [additionalFields]);

  return {
    isLoadingCustomAttrs,
    additionalFields,
    orderedCustomAttributes,
  };
};
